/**
 * Dashboard Page for Admin PWA
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  DollarSign, 
  ShoppingCart, 
  TrendingUp, 
  Users,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Clock,
  PieChart
} from 'lucide-react';
import { useBranch } from '../context/BranchContext';
import { SimpleBarChart, SimpleDonutChart } from '../components/Charts';
import { accountHeaders } from '@/lib/pwaAccount';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getBranchHeaders } = useBranch();


  const getHeaders = useCallback(() => ({
    ...accountHeaders(),
    ...getBranchHeaders()
  }), [getBranchHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/dashboard-stats`, {
        headers: getHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  const fetchHourlySales = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/sales-by-hour`, {
        headers: getHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        // Transform to chart format - only show hours with data
        const chartData = data
          .filter(d => d.revenue > 0 || d.orders > 0)
          .map(d => ({
            label: `${d.hour}:00`,
            value: d.revenue
          }));
        setHourlyData(chartData.length > 0 ? chartData : data.slice(8, 22).map(d => ({
          label: `${d.hour}:00`,
          value: d.revenue
        })));
      }
    } catch (err) {
      console.error('Failed to fetch hourly sales:', err);
    }
  }, [getHeaders]);

  const fetchOrderTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/order-types?period=week`, {
        headers: getHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        const colors = {
          pickup: '#8b5cf6',
          delivery: '#3b82f6',
          dine_in: '#f97316',
          unknown: '#64748b'
        };
        setOrderTypes(data.map(d => ({
          label: d.type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()),
          value: d.count,
          color: colors[d.type] || colors.unknown
        })));
      }
    } catch (err) {
      console.error('Failed to fetch order types:', err);
    }
  }, [getHeaders]);

  const fetchTopItems = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/top-items?period=week&limit=5`, {
        headers: getHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setTopItems(data);
      }
    } catch (err) {
      console.error('Failed to fetch top items:', err);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchStats();
    fetchHourlySales();
    fetchOrderTypes();
    fetchTopItems();
  }, [fetchStats, fetchHourlySales, fetchOrderTypes, fetchTopItems]);

  const statCards = [
    {
      title: "Today's Revenue",
      value: stats?.todayRevenue || '$0',
      change: stats?.revenueChange || '+0%',
      changeType: 'positive',
      icon: DollarSign,
      color: 'emerald'
    },
    {
      title: 'Orders Today',
      value: stats?.todayOrders || '0',
      change: stats?.ordersChange || '+0%',
      changeType: 'positive',
      icon: ShoppingCart,
      color: 'blue'
    },
    {
      title: 'Avg Order Value',
      value: stats?.avgOrderValue || '$0',
      change: stats?.aovChange || '+0%',
      changeType: 'neutral',
      icon: TrendingUp,
      color: 'purple'
    },
    {
      title: 'Active Staff',
      value: stats?.activeStaff || '0',
      change: 'On shift',
      changeType: 'neutral',
      icon: Users,
      color: 'amber'
    }
  ];

  const colorMap = {
    emerald: 'bg-emerald-500/10 text-emerald-500',
    blue: 'bg-blue-500/10 text-blue-500',
    purple: 'bg-purple-500/10 text-purple-500',
    amber: 'bg-amber-500/10 text-amber-500'
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 text-sm mt-1">
          Welcome back! Here's what's happening today.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        {statCards.map((stat, i) => (
          <Card key={i} className="bg-slate-900 border-slate-800">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-400 truncate">{stat.title}</p>
                  <p className="text-lg sm:text-2xl font-bold text-white mt-0.5 sm:mt-1 truncate">{stat.value}</p>
                  <div className="flex items-center gap-1 mt-1 sm:mt-2">
                    {stat.changeType === 'positive' ? (
                      <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                    ) : stat.changeType === 'negative' ? (
                      <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 flex-shrink-0" />
                    ) : null}
                    <span className={`text-xs sm:text-sm truncate ${
                      stat.changeType === 'positive' ? 'text-emerald-500' :
                      stat.changeType === 'negative' ? 'text-red-500' :
                      'text-slate-500'
                    }`}>
                      {stat.change}
                    </span>
                  </div>
                </div>
                <div className={`p-2 sm:p-3 rounded-lg ${colorMap[stat.color]} ml-2 flex-shrink-0`}>
                  <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Hourly Sales Chart */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
            <CardTitle className="text-white text-sm sm:text-lg flex items-center gap-2">
              <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              Today's Sales by Hour
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {hourlyData.length > 0 ? (
              <SimpleBarChart data={hourlyData} height={160} barColor="#10b981" />
            ) : (
              <div className="text-slate-400 text-xs sm:text-sm text-center py-8 sm:py-12">
                No sales data for today
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Types Chart */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
            <CardTitle className="text-white text-sm sm:text-lg flex items-center gap-2">
              <PieChart className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              Orders by Type (This Week)
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {orderTypes.length > 0 ? (
              <SimpleDonutChart data={orderTypes} size={120} thickness={20} />
            ) : (
              <div className="text-slate-400 text-xs sm:text-sm text-center py-8 sm:py-12">
                No order data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Recent orders */}
        <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
          <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
            <CardTitle className="text-white text-sm sm:text-lg flex items-center gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              Recent Orders
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {loading ? (
              <div className="space-y-2 sm:space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="h-10 sm:h-12 bg-slate-800 rounded animate-pulse" />
                ))}
              </div>
            ) : stats?.recentOrders?.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {stats.recentOrders.slice(0, 5).map((order) => (
                  <div 
                    key={order.id} 
                    className="flex items-center justify-between p-2 sm:p-3 bg-slate-800/50 rounded-lg"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium flex-shrink-0 ${
                        order.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                        order.status === 'ready' ? 'bg-blue-500/20 text-blue-400' :
                        order.status === 'preparing' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-700 text-slate-400'
                      }`}>
                        {order.status}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-xs sm:text-sm font-medium truncate">{order.orderNumber}</p>
                        <p className="text-slate-500 text-[10px] sm:text-xs truncate">{order.type}</p>
                      </div>
                    </div>
                    <span className="text-white text-xs sm:text-sm font-medium ml-2 flex-shrink-0">{order.total}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-slate-400 text-xs sm:text-sm text-center py-6 sm:py-8">
                No recent orders
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Items */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
            <CardTitle className="text-white text-sm sm:text-lg flex items-center gap-2">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              Top Items
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {topItems.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {topItems.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className="text-slate-500 text-xs sm:text-sm w-3 sm:w-4 flex-shrink-0">{i + 1}.</span>
                      <span className="text-white text-xs sm:text-sm truncate">{item.name}</span>
                    </div>
                    <div className="text-right ml-2 flex-shrink-0">
                      <p className="text-emerald-400 text-xs sm:text-sm font-medium">{item.revenue}</p>
                      <p className="text-slate-500 text-[10px] sm:text-xs">{item.quantity} sold</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-slate-400 text-xs sm:text-sm text-center py-6 sm:py-8">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
